import React, {useCallback, useEffect, useState} from 'react';
import {createStyles, makeStyles, Theme, useTheme} from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import { debounce } from 'throttle-debounce';
import {
  Avatar,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  Switch,
  withStyles,
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Slider from "@material-ui/core/Slider";
import Input from "@material-ui/core/Input";
import {useSelector, useDispatch} from 'react-redux';
import {
  ColorMapKey, ColorType,
  selectCloudDistance, changeCloudDistance,
  selectOpacity, changeOpacity,
  selectCloudSize, changeCloudSize,
  selectHeightClip, changeHeightClip,
  selectIntensityClip, changeIntensityClip,
  selectColorMap, changeColorMap,
  selectColorType, changeColorType,
  selectDynamicSize, changeDynamicSize,
  selectAvailableClassifications, changeClassificationVisibility,
  selectClassificationVisibilities, changeAllClassificationVisibility,
  changeCircularPoints, selectCircularPoints,
  changeArrowMarkers, selectArrowMarkers, MarkerNavType,
  selectCloudMaxPoints, changeCloudMaxPoints
} from './redux/settings-slice';
import {colorMaps} from "./viewer/textures/colormaps";
import { getClassificationColors } from "./classifications";
import { isDevMode } from './electron-modules';
import { ArrowTooltip } from './tooltips';
import { Viewer } from './viewer/js/main';
import { SlimScrollbar } from './toolbar';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import LocalScene from "./viewer/js/projections";
import {UnitConverter} from "./viewer/js/utilities";

export const settingsDrawerWidth = 280;

const converter = new UnitConverter();

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      display: "none"
    },
    drawer: {
      width: 0,
    },
    drawerPaper: {
      width: settingsDrawerWidth,
    },
    asterisk: {
      "&:after": {
        content:'"*"',
        color: "red",
        paddingLeft: theme.spacing(0.5)
      }
    },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
      justifyContent: 'flex-start',
    },
    list: {
      width: '100%',
      maxWidth: 360,
      backgroundColor: theme.palette.background.paper,
    },
    listItem: {
      width: '100%',
      marginBottom: theme.spacing(0.5)
    },
    formControl: {
      width: '100%',
      marginBottom: theme.spacing(1)
    },
    switch: {
      marginLeft: 0
    },
    colorPreview: {
      height: theme.spacing(3),
      width: theme.spacing(3),
      marginRight: theme.spacing(1),
    },
    img: {
      objectFit: 'fill',
      transform: 'rotate(-90deg)'
    },
    classificationPreview: {
      height: theme.spacing(1.25),
      width: theme.spacing(1.25),
      backgroundColor: '#bbb',
      borderRadius: '50%',
      borderColor: '#000000',
      borderStyle: 'solid',
      borderWidth: '1px',
      marginRight: theme.spacing(1),
      display: 'inline-block'
    },
    switchLabel: {
      display: "inline-block"
    },
    switchElement: {
      float: "right"
    },
    inputText: {
      float: "right",
      paddingRight: "10px"
    },
    inputPropDiv: {
      display: "flex",
      background: "rgba(200, 200, 200, 0.1)",
      boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
      zoom: "0.85",
      padding: "6px 12px",
      marginLeft: "12px",
      marginRight: "calc(28px + 12px)",
      marginTop: "12px"
    },
    inputLabel: {
      margin: "auto 12px auto 0px"
    },
    boldTypography: {
      fontWeight: 'bold',
      display: "inline-block",
      fontSize: '1em'
    },
    rightInput: {
      marginRight: "4px"
    },
    sliderInputParent: {
      display: "flex",
      paddingTop: theme.spacing(1.0)
    },
    sliderInputToggle: {
      padding: "0px",
      margin: "9px 0px 0px 6px"
    }
  }),
);

const SingleSlider = (props) => {
  const dispatchInterval = 1000;
  const dispatch = useDispatch();
  const saveFunction = useCallback(
    debounce(dispatchInterval, (value) => {
      dispatch(props.changeFunction(value));
    }), []);
  return (
    <InputSlider type="single" saveFunction={saveFunction} {...props}/>
  );
};

const DistanceSlider = (props) => {
  const dispatchInterval = 1000;
  const dispatch = useDispatch();
  const saveFunction = useCallback(
    debounce(dispatchInterval, (value) => {
      dispatch(props.changeFunction(value));
    }), []);

  return (
    <InputSlider type="distance" saveFunction={saveFunction} {...props}/>
  );
};

const RangeSlider = (props) => {
  const dispatchInterval = 1000;
  const dispatch = useDispatch();
  const saveFunction = useCallback(
    debounce(dispatchInterval, (value) => {
      dispatch(props.changeFunction(value));
    }), []);
  return (
    <InputSlider type="range" saveFunction={saveFunction} {...props}/>
  );
};

export const ViewerSettingsSlider = (props) => {
  const nullFunction = (val) => {};
  return (
    <InputSlider type="single" saveFunction={nullFunction} {...props}/>
  );
};

const InputSlider = (props) => {
  const {value, step, min, max, updateFunction, saveFunction, setFunction, labelFunction, endAdornment, magnitude, units, type} = props;
  const classes = useStyles();
  const labelFormat = (!labelFunction) ? (value) => {return value*magnitude;} : labelFunction;
  const isRangeType = (type === "range");
  const isDistanceType = (type === "distance");
  const adornment = (endAdornment) ? endAdornment : "";
  const marks = [{
    value: min,
    label: labelFormat(min)
  }, {
    value: max,
    label: labelFormat(max)
  }];

  const toMag = (val) => {
    return +(val*magnitude).toFixed(2);
  };

  const setValues = (value) => {
    updateFunction(value);
    saveFunction(value);
    setInputValue(toMag(value));
    setFunction(value);
  };

  const setRangeValues = (lowerVal, upperVal) => {
    updateFunction({min: lowerVal, max: upperVal});
    saveFunction({min: lowerVal, max: upperVal});
    setLower(toMag(lowerVal));
    setUpper(toMag(upperVal));
    setFunction({min: lowerVal, max: upperVal});
  };

  const [inputValue, setInputValue] = useState(isDistanceType ? converter.convert(value, "m", units) : toMag(value));
  const [lowerInputValue, setLower] = useState(value && value.length === 2 ? toMag(value[0]) : toMag(value));
  const [upperInputValue, setUpper] = useState(value && value.length === 2 ? toMag(value[1]) : toMag(value));

  const [openState, setOpen] = useState(false);
  const [errorState, setError] = useState(isRangeType ? [false, false] : false);


  const validInput = (value) => {
    let error = false;
    if (value === '' || isNaN(value)) {
      return ['', true];
    }
    if (isDistanceType && (value < converter.convert(vals.min, "m", units)
      || value > converter.convert(vals.max, "m", units))) {
      error = true;
    } else if (value < vals.min || value > vals.max) {
      error = true;
    }
    return [value, error];
  };

  const convertedInputValue = (value) => {
    let newValues = validInput(value);
    if (newValues[0] === '') {
      return '';
    }
    return converter.convert(newValues[0], "m", units, 1);
  };

  const handleSingleChange = (newValue) => {
    let [value, error] = validInput(newValue);
    if (error) {
      setInputValue(value);
    } else if (isDistanceType) {
      let cv = Number(converter.convert(value, units, 'm'));
      updateFunction(converter.convert(cv, units, 'm'));
      saveFunction(cv);
      setFunction(cv);
      setInputValue(cv);
    } else {
      setValues(value / magnitude);
    }
    setError(error);
  };

  const handleLowerRangeChange = (newValue) => {
    let [value, error] = validInput(newValue);

    if (error || value > Number(upperInputValue)) {
      setLower(value);
      error = true;
    } else {
      setRangeValues(value/magnitude, Number(upperInputValue)/magnitude);
    }
    setError([error, errorState[1]]);
  };

  const handleUpperRangeChange = (newValue) => {
    let [value, error] = validInput(newValue);
    if (error || value < Number(lowerInputValue)) {
      setUpper(value);
      error = true;
    } else {
      setRangeValues(Number(lowerInputValue)/magnitude, value/magnitude);
    }
    setError([errorState[0], error]);
  };

  const handleSliderChange = (event, newValue) => {
    if (isRangeType) {
      setRangeValues(newValue[0], newValue[1]);
      setError([false, false]);
    } else if (isDistanceType) {
      updateFunction(converter.convert(newValue, units, 'm'));
      setFunction(newValue);
      saveFunction(newValue);
      setInputValue(newValue);
    } else {
      setValues(newValue);
    }
    setError(false);
  };

  const handleClick = () => {
    setOpen(!openState);
  };

  let vals = {
    min: isDistanceType ? converter.convert(min, "m", units) : min*magnitude,
    max: isDistanceType ? converter.convert(max, "m", units) : max*magnitude,
    step: step*magnitude,
  };

  return (
    <React.Fragment>
      <div className={classes.sliderInputParent}>
        <StyledSlider
          color={"secondary"}
          min={min}
          max={max}
          step={step}
          marks={marks}
          value={value}
          valueLabelDisplay="on"
          valueLabelFormat={labelFormat}
          onChange={handleSliderChange}
          aria-labelledby="input-slider"
        />

        <IconButton
          size="small"
          className={classes.sliderInputToggle}
          onClick={(event) => {
            event.stopPropagation();
            handleClick();
          }}
        >
          {openState ? <KeyboardArrowUp  fontSize="small"/> : <KeyboardArrowDown fontSize="small"/>}
        </IconButton>
      </div>

      {openState && (
        <div className={classes.listItem}>
          {isRangeType ? <RangeInputProp
            lower={lowerInputValue}
            upper={upperInputValue}
            vals={vals}
            errorState={errorState}
            adornment={adornment}
            onChangeLower={handleLowerRangeChange}
            onChangeUpper={handleUpperRangeChange}
          /> : <InputProp
            value={isDistanceType ? convertedInputValue(inputValue) : inputValue}
            vals={vals}
            errorState={errorState}
            adornment={adornment}
            onChange={handleSingleChange}
          />
          }
        </div>
      )}
    </React.Fragment>
  );
};

const StyledSlider = withStyles({
  root: {
    marginBottom: "0px",
    marginLeft: "12px",
    marginRight: "12px"
  },
  rail: {
    height: "12px",
    borderRadius: "4px"
  },
  track: {
    height: "12px",
    borderRadius: "0px",
    borderTopLeftRadius: "4px",
    borderBottomLeftRadius: "4px"
  },
  thumb: {
    height: "18px",
    width: "2px",
    marginTop: "-3px",
    marginLeft: "0px",
    borderRadius: "0px",
    '&:focus, &:hover, &$active': {
      boxShadow: 'inherit',
    },
  },
  valueLabel: {
    top: '-8px',
    left: 'calc(-50% - 18px)',
    height: '18px',
    width: '40px',
    '& *': {
      transform: 'rotate(0)',
      borderRadius: '4px',
      height: 'auto',
      width: '40px',
      textAlign: 'center'
    },
    '&::after': {
      position: "absolute",
      display: "block",
      content: '""',
      bottom: "-2px",
      left: "50%",
      width: "0",
      height: "0",
      marginLeft: "-3px",
      overflow: "hidden",
      border: "3px solid transparent",
      borderTopColor: "#dd0c29"
    }
  },
  mark: {
    opacity: 0,
  },
  markLabel: {
    top: "-8px",
    fontSize: "0.75em",
    width: "32px",
    height: "14px",
    textAlign: "center",
    backgroundColor:  "rgba(0,0,0,0.2)",
    borderRadius: "4px",
    opacity: 0.5
  },
  markLabelActive: {
    opacity: 0.5
  },
  active: {}
})(Slider);

const InputProp = (props) => {
  const {value, vals, errorState, adornment, onChange} = props;
  const classes = useStyles();

  return (
    <div className={classes.inputPropDiv}>
      <InputLabel className={classes.inputLabel}>Value:</InputLabel>

      <InputField
        vals={vals}
        value={value}
        label={"Input Value"}
        errorState={errorState}
        adornment={adornment}
        onChange={(value) => {
          onChange(value);
        }}
      />
    </div>
  );
};

const RangeInputProp = (props) => {
  const {vals, lower, upper, onChangeLower, onChangeUpper, adornment, errorState} = props;
  const classes = useStyles();

  return (
    <React.Fragment>
      <div className={classes.inputPropDiv}>
        <InputLabel>Minimum Value:</InputLabel>

        <InputField
          value={lower}
          vals={vals}
          adornment={adornment}
          errorState={errorState[0]}
          onChange={(value) => {
            onChangeLower(value);
          }}
        />
      </div>

      <div className={classes.inputPropDiv}>
        <InputLabel>Maximum Value:</InputLabel>

        <InputField
          value={upper}
          vals={vals}
          adornment={adornment}
          errorState={errorState[1]}
          onChange={(value) => {
            onChangeUpper(value);
          }}
        />

      </div>
    </React.Fragment>
  );
};

const InputField = (props) => {
  const {value, vals, onChange, adornment, errorState} = props;

  return (
    <Input
      fullWidth
      error={errorState}
      inputProps={{
        max: vals.max,
        min: vals.min,
        step: vals.step,
        type: "number"
      }}
      value={value}
      endAdornment={<InputAdornment position="end">{adornment}</InputAdornment>}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
};

export const SettingsDrawer = (props) => {
  const viewer = props.viewer as Viewer;

  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();
  const colorMap = useSelector(selectColorMap);
  const colorType = useSelector(selectColorType);
  const dynamicSize = useSelector(selectDynamicSize);
  const circularPoints = useSelector(selectCircularPoints);
  const arrowMarkers = useSelector(selectArrowMarkers);
  const availableClassifications = useSelector(selectAvailableClassifications);
  const classificationVisibilities = useSelector(selectClassificationVisibilities);
  const [classificationColors, setClassificationColors] = useState(Array(256).fill('#ffffff'));
  const [pickerDebug, setPickerDebug] = useState(false);
  const units = LocalScene.viewProjectionUnits;

  const getClassifications = (classifications) => {
    if (classifications.length === 0) {
      return <div>
        <ListItem>
          <Typography gutterBottom variant="subtitle2">
            No points loaded
          </Typography>
        </ListItem>
      </div>;
    }

    return classifications.map(classification => (
      <ListItem key={classification.id}>
        <div className={classes.listItem}>
          <div>
            <span
              className={classes.classificationPreview}
              style={{
                backgroundColor: classificationColors[classification.id]
              }}
            />

            <Typography
              gutterBottom
              variant="subtitle2"
              className={classes.switchLabel}
            >
              {`${classification.name} (${classification.id})`}
            </Typography>

            <Switch
              checked={classification.visible}
              className={classes.switchElement}
              size="small"
              color="secondary"
              onChange={() => {
                dispatch(changeClassificationVisibility(classification.id));
              }}
            />
          </div>
        </div>
      </ListItem>
    ));
  };

  /** Cloud Opacity */
  const opacity = useSelector(selectOpacity);
  const [opacitySliderValue, setOpacitySlider] = useState(0);

  /** Cloud Distance */
  const cloudDistance = useSelector(selectCloudDistance);
  const [distanceSliderValue, setDistanceSlider] = useState(0);


  /** Cloud Size */
  const cloudSize = useSelector(selectCloudSize);
  const [cloudSizeSlider, setCloudSizeSlider] = useState(0);

  /** Cloud Size */
  const maxPoints = useSelector(selectCloudMaxPoints);
  const [maxPointsSlider, setMaxPointsSlider] = useState(0);


  /** Height Range */
  const heightClip = useSelector(selectHeightClip);
  const [heightClipSlider, setHeightClipSlider] = useState({min: 0,max: 0});


  /** Intensity Range */
  const intensityClip = useSelector(selectIntensityClip);
  const [intensityClipSlider, setIntensityClipSlider] = useState({min: 0,max: 0});

  const updateOpacity = (value) => {
    setOpacitySlider(value);
    viewer?.setPointCloudOpacity(value);
  };

  const updateDistance = (value) => {
    setDistanceSlider(converter.convert(value, 'm', units));
    viewer?.setPointCloudDistance(value);
  };

  const updateCloudSize = (value) => {
    setCloudSizeSlider(value);
    viewer?.setPointCloudSize(value);
  };

  const updateMaxPoints = (value) => {
    setMaxPointsSlider(value);
    viewer?.setPointCloudMaxPoints(value);
  };

  const updateHeightClip = (value) => {
    setHeightClipSlider(value);
    viewer?.setPointCloudHeightClip(value);
  };

  const updateIntensityClip = (value) => {
    setIntensityClipSlider(value);
    viewer?.setPointCloudIntensityClip(value);
  };

  useEffect(() => {
    updateOpacity(opacity);
  }, [viewer, opacity]);

  useEffect(() => {
    updateDistance(converter.convert(cloudDistance, units, 'm'));
  }, [viewer, cloudDistance]);

  useEffect(() => {
    updateCloudSize(cloudSize);
  }, [viewer, cloudSize]);

  useEffect(() => {
    updateMaxPoints(maxPoints);
  }, [viewer, maxPoints]);

  useEffect(() => {
    updateHeightClip({min: heightClip.min, max: heightClip.max});
  }, [viewer, heightClip]);

  useEffect(() => {
    updateIntensityClip({min: intensityClip.min, max: intensityClip.max});
  }, [viewer, intensityClip]);

  useEffect(() => {
    viewer?.setVisibleClassifications(classificationVisibilities);
  }, [viewer, classificationVisibilities]);

  useEffect(() => {
    viewer?.setPointCloudColorMap(colorMap);
  }, [viewer, colorMap]);

  useEffect(() => {
    viewer?.setPointCloudColorType(colorType);
  }, [viewer, colorType]);

  useEffect(() => {
    viewer?.setPointCloudDynamicSize(dynamicSize);
  }, [viewer, dynamicSize]);

  useEffect(() => {
    viewer?.setPointCloudCircularPoints(circularPoints);
  }, [viewer, circularPoints]);

  useEffect(() => {
    viewer?.setNavigationType(arrowMarkers);
  }, [viewer, arrowMarkers]);

  useEffect(() => {
    getClassificationColors().then(result => {
      setClassificationColors(result);
    });
  }, []);

  const isIntensity = colorType === 0;
  const isHeightmap = colorType === 2;
  const colorMapRequired = isIntensity || isHeightmap;

  return (
    <Drawer
      className={classes.drawer}
      anchor="right"
      open={props.open}
      transitionDuration={0}
      classes={{
        paper: classes.drawerPaper,
      }}
      BackdropProps={{
        classes: {
          root: classes.backdrop,
        },
      }}
    >
      <div className={classes.drawerHeader}>
        <IconButton onClick={props.handleClose}>
          {theme.direction === "rtl" ? (
            <ChevronLeftIcon />
          ) : (
            <ChevronRightIcon />
          )}
        </IconButton>
      </div>

      <Divider />

      <SlimScrollbar>
        {/* Point Cloud Controls */}
        <List
          dense
          aria-labelledby="nested-list-subheader"
          className={classes.list}
        >
          <ListItem>
            <Typography className={classes.boldTypography} gutterBottom>
              Point Cloud Controls
            </Typography>
          </ListItem>

          {/* Color type */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                Color Type
              </Typography>
              <FormControl className={classes.formControl}>
                <Select
                  value={colorType}
                  onChange={(event) => {
                    const value = event.target.value as ColorType;
                    dispatch(changeColorType(value));
                  }}
                >
                  <MenuItem key={0} value={0}>
                    Intensity
                  </MenuItem>
                  <MenuItem key={1} value={1}>
                    Color
                  </MenuItem>
                  <MenuItem key={2} value={2}>
                    Height
                  </MenuItem>
                  <MenuItem key={3} value={3}>
                    Classification
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
          </ListItem>

          {/* Color maps */}
          {colorMapRequired && (
            <ListItem>
              <div className={classes.listItem}>
                <Typography gutterBottom variant="subtitle2">
                  Color Map
                </Typography>
                <FormControl className={classes.formControl}>
                  <Select
                    value={colorMap}
                    renderValue={(value) =>
                      colorMaps.find((colorMap) => colorMap.id === value).name
                    }
                    onChange={(event) => {
                      const value = event.target.value as ColorMapKey;
                      dispatch(changeColorMap(value));
                    }}
                  >
                    {colorMaps.map((colorMap) => (
                      <MenuItem key={colorMap.id} value={colorMap.id}>
                        <Avatar
                          classes={{ img: classes.img }}
                          src={colorMap.image}
                          className={classes.colorPreview}
                        />
                        {colorMap.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </ListItem>
          )}

          {/* Arrow Markers */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                Navigation
              </Typography>
              <FormControl className={classes.formControl}>
                <Select
                  value={arrowMarkers}
                  onChange={(event) => {
                    let value = event.target.value as MarkerNavType;
                    dispatch(changeArrowMarkers(value));
                  }}
                >
                  <MenuItem key={0} value={MarkerNavType.Arrows}>
                    Arrows
                  </MenuItem>
                  <MenuItem key={1} value={MarkerNavType.Markers}>
                    Markers
                  </MenuItem>
                  <MenuItem key={2} value={MarkerNavType.None}>
                    None
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
          </ListItem>

          {/* Cloud Opacity */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                Point Visibility
              </Typography>
              <SingleSlider
                color={"secondary"}
                value={opacitySliderValue}
                min={0}
                max={1}
                step={0.01}
                magnitude={100}
                endAdornment={"%"}
                labelFunction={(value) => {
                  return +Number(value*100).toFixed(1)+"%";
                }}
                changeFunction={changeOpacity}
                setFunction={setOpacitySlider}
                updateFunction={(value) => {
                  viewer?.setPointCloudOpacity(value);
                }}
              />
            </div>
          </ListItem>

          {/* Cloud Max Points */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                Max Visible Points
              </Typography>
              <SingleSlider
                color={"secondary"}
                value={maxPointsSlider}
                min={0}
                max={10}
                step={0.05}
                magnitude={1}
                endAdornment={"M"}
                labelFunction={(value) => {
                  return +Number(value).toFixed(2)+"M";
                }}
                changeFunction={changeCloudMaxPoints}
                setFunction={setMaxPointsSlider}
                updateFunction={(value) => {
                  viewer?.setPointCloudMaxPoints(value);
                }}
              />
            </div>
          </ListItem>

          {/* Cloud Distance */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                Distance
              </Typography>
              <DistanceSlider
                color={"secondary"}
                value={distanceSliderValue}
                min={0}
                max={1000}
                step={5}
                magnitude={1}
                units={units}
                endAdornment={units}
                labelFunction={(value) => {
                  if (value > 999) {
                    return "MAX";
                  } else {
                    const converted = converter.convert(value, "m", units, 0);
                    return `${converted}${units}`;
                  }
                }}
                changeFunction={changeCloudDistance}
                setFunction={setDistanceSlider}
                updateFunction={(value) => {
                  viewer?.setPointCloudDistance(value);
                }}
              />
            </div>
          </ListItem>

          {/* Cloud Size */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                Size
              </Typography>
              <SingleSlider
                color={"secondary"}
                value={cloudSizeSlider}
                min={0}
                max={10}
                step={0.1}
                magnitude={1}
                changeFunction={changeCloudSize}
                setFunction={setCloudSizeSlider}
                updateFunction={(value) => {
                  viewer?.setPointCloudSize(value);
                }}
              />
            </div>
          </ListItem>

          {/* Height range */}
          {isHeightmap && (
            <ListItem>
              <div className={classes.listItem}>
                <Typography gutterBottom variant="subtitle2">
                  Height Range
                </Typography>
                <RangeSlider
                  color={"secondary"}
                  value={[heightClipSlider.min, heightClipSlider.max]}
                  min={-0.5}
                  max={1.5}
                  step={0.01}
                  magnitude={100}
                  endAdornment={"%"}
                  labelFunction={(value) => {
                    return +Number(value*100).toFixed(1)+"%";
                  }}
                  valueLabelDisplay="on"
                  changeFunction={changeHeightClip}
                  setFunction={setHeightClipSlider}
                  updateFunction={(value) => {
                    viewer?.setPointCloudHeightClip(value);
                  }}
                />
              </div>
            </ListItem>
          )}

          {/* Intensity range */}
          {isIntensity && (
            <ListItem>
              <div className={classes.listItem}>
                <Typography gutterBottom variant="subtitle2">
                  Intensity Range
                </Typography>
                <RangeSlider
                  color={"secondary"}
                  value={[intensityClipSlider.min, intensityClipSlider.max]}
                  min={-0.5}
                  max={1.5}
                  step={0.01}
                  magnitude={100}
                  endAdornment={"%"}
                  labelFunction={(value) => {
                    return +Number(value*100).toFixed(1)+"%";
                  }}
                  valueLabelDisplay="on"
                  changeFunction={changeIntensityClip}
                  setFunction={setIntensityClipSlider}
                  updateFunction={(value) => {
                    viewer?.setPointCloudIntensityClip(value);
                  }}
                />
              </div>
            </ListItem>
          )}

          {/* Dynamic size */}
          <ListItem>
            <div className={classes.listItem}>
              <ArrowTooltip
                title="Not applicable for raw las files"
                placement="top"
              >
                <Typography
                  gutterBottom
                  variant="subtitle2"
                  className={classes.switchLabel}
                  classes={{
                    subtitle2: classes.asterisk,
                  }}
                >
                  Dynamic Point Sizing
                </Typography>
              </ArrowTooltip>

              <Switch
                checked={dynamicSize}
                className={classes.switchElement}
                size="small"
                color="secondary"
                onChange={(_, checked) => {
                  dispatch(changeDynamicSize(checked));
                }}
              />
            </div>
          </ListItem>

          {/* Circular Points */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography
                gutterBottom
                variant="subtitle2"
                className={classes.switchLabel}
              >
                Circular Points
              </Typography>

              <Switch
                checked={circularPoints}
                className={classes.switchElement}
                size="small"
                color="secondary"
                onChange={(_, checked) => {
                  dispatch(changeCircularPoints(checked));
                }}
              />
            </div>
          </ListItem>

          {/* Picker Debug */}
          {isDevMode && (
            <ListItem>
              <div className={classes.listItem}>
                <Typography
                  gutterBottom
                  variant="subtitle2"
                  className={classes.switchLabel}
                >
                  Picker Debug
                </Typography>

                <Switch
                  checked={pickerDebug}
                  className={classes.switchElement}
                  size="small"
                  color="secondary"
                  onChange={(_, checked) => {
                    viewer?.togglePickerDebug(checked);
                    setPickerDebug(checked);
                  }}
                />
              </div>
            </ListItem>
          )}
        </List>

        <Divider variant="middle" />

        {/* Classifications */}
        <List>
          <ListItem>
            <div className={classes.listItem}>
              <Typography
                gutterBottom
                variant="subtitle2"
                className={classes.boldTypography}
              >
                Classifications
              </Typography>

              {availableClassifications.length > 0 && (
                <ArrowTooltip title="Toggle All Classifications" placement="top">
                  <Switch
                    checked={availableClassifications.every(
                      (classification) => classification.visible
                    )}
                    className={classes.switchElement}
                    size="small"
                    color="secondary"
                    onChange={(_, checked) => {
                      dispatch(changeAllClassificationVisibility(checked));
                    }}
                  />
                </ArrowTooltip>
              )}
            </div>
          </ListItem>
          {getClassifications(availableClassifications)}
        </List>

      </SlimScrollbar>
    </Drawer>
  );
};